import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { errorMessage } from './format';

const PAGE_SIZE = 25;

/**
 * Side panel listing the records behind a metric.
 *
 * `fetchPage({ page, limit })` returns { items, total }. `describe(item)`
 * returns { key, title, subtitle, meta, href } for one row.
 */
const DrilldownDrawer = ({ open, title, subtitle, fetchPage, describe, onClose }) => {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ items: [], total: 0, loading: false, error: null });
  const closeRef = useRef(null);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, title]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    fetchPage({ page, limit: PAGE_SIZE })
      .then((res) => {
        if (!cancelled) setState({ items: res?.items || [], total: res?.total || 0, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({ items: [], total: 0, loading: false, error: errorMessage(err, 'Could not load records.') });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, page, fetchPage]);

  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const first = state.total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const last = Math.min(page * PAGE_SIZE, state.total);

  return (
    <div className="fixed inset-0 z-[70] flex justify-end">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-black/30 cursor-default"
        onClick={onClose}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative w-full max-w-[520px] h-full bg-white shadow-2xl flex flex-col"
      >
        <header className="flex items-start justify-between gap-4 p-6 border-b border-gray-200">
          <div className="flex flex-col gap-1">
            <h2 className="m-0 text-lg font-bold text-gray-900">
              {title}
              {!state.loading && !state.error && ` · ${state.total}`}
            </h2>
            {subtitle && <p className="m-0 text-sm text-gray-600">{subtitle}</p>}
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-11 h-11 flex items-center justify-center rounded-md border border-gray-300 hover:bg-gray-50"
          >
            <X size={18} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2">
          {state.loading && <p className="text-sm text-gray-600 p-2">Loading…</p>}
          {state.error && <p className="text-sm text-red-700 p-2">{state.error}</p>}
          {!state.loading && !state.error && state.items.length === 0 && (
            <p className="text-sm text-gray-600 p-2">No records for this period.</p>
          )}
          {!state.loading &&
            !state.error &&
            state.items.map((item) => {
              const row = describe(item);
              const body = (
                <>
                  <span className="flex justify-between gap-3">
                    <span className="font-semibold text-gray-900">{row.title || '—'}</span>
                    {row.meta && <span className="text-sm text-gray-600 shrink-0">{row.meta}</span>}
                  </span>
                  {row.subtitle && <span className="text-sm text-gray-600">{row.subtitle}</span>}
                </>
              );
              return row.href ? (
                <Link
                  key={row.key}
                  to={row.href}
                  className="flex flex-col gap-1 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 no-underline"
                >
                  {body}
                </Link>
              ) : (
                <div key={row.key} className="flex flex-col gap-1 p-3 rounded-lg border border-gray-200">
                  {body}
                </div>
              );
            })}
        </div>

        <footer className="flex items-center justify-between gap-3 p-4 border-t border-gray-200 text-sm text-gray-700">
          <span>{state.total ? `${first}–${last} of ${state.total}` : ''}</span>
          <span className="flex gap-2">
            <button
              type="button"
              disabled={page <= 1 || state.loading}
              onClick={() => setPage((p) => p - 1)}
              className="min-h-[40px] px-3 rounded-md border border-gray-300 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={last >= state.total || state.loading}
              onClick={() => setPage((p) => p + 1)}
              className="min-h-[40px] px-3 rounded-md border border-gray-300 disabled:opacity-40"
            >
              Next
            </button>
          </span>
        </footer>
      </aside>
    </div>
  );
};

export default DrilldownDrawer;
